import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import {
  Mic,
  PenLine,
  Zap,
  ArrowRight,
  ArrowLeft,
  X,
  MapPin,
  CalendarDays,
  Truck,
  ClipboardList,
  CheckCircle2,
  Lock,
  Globe2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { PostTypeCardGrid, VisibilityCardGrid } from "@/components/lr-direct/PostStartCards";
import { saveDirectLoadPost, type GuidedPlace, type GuidedStop } from "@/lib/lrDirect/saveDirectLoadPost";
import { getTruckRoute, getMultiStopRoute } from "@/lib/mapbox";
import { useDefaultEquipment } from "@/hooks/lrDirect/useDefaultEquipment";
import {
  defaultWizardFields,
  type WizardFields,
  type WizardStop,
} from "@/components/lr-direct/guided-wizard/wizardTypes";
import WizardRouteStep from "@/components/lr-direct/guided-wizard/WizardRouteStep";
import WizardDatesStep from "@/components/lr-direct/guided-wizard/WizardDatesStep";
import WizardEquipmentStep from "@/components/lr-direct/guided-wizard/WizardEquipmentStep";
import WizardExtrasStep from "@/components/lr-direct/guided-wizard/WizardExtrasStep";
import WizardReviewStep from "@/components/lr-direct/guided-wizard/WizardReviewStep";
import WizardVoiceCapture from "@/components/lr-direct/guided-wizard/WizardVoiceCapture";

/**
 * LR Direct — GUIDED POSTING WIZARD (Tier 3).
 *
 * Flow: Step 0 mode selector (Voice / Type) → Step 1 the existing 2 cards
 * (Load vs Quote → Private vs Open, reused from PostStartCards) → Steps 2–5
 * real fields for TYPE mode (Route with multi-stop generation, Dates,
 * Equipment & Weight, Extras — all reusing the form's field components) →
 * Step 6 Review with editable auto-derived deadline → POST via the shared
 * save path (saveDirectLoadPost — the same direct_loads + direct_load_stops
 * insert and broker fan-out the form uses).
 *
 * VOICE mode collapses steps 2–5 into ONE capture screen (WizardVoiceCapture)
 * and jumps 2 → 6: "tell Atlas about your load" → load-being-built card with
 * tap/voice corrections → gating blockers reported via onGateChange → Review.
 * State is held in the component for the whole flow, so Back never loses
 * selections.
 *
 * SHIPPER-ONLY: the route (/lr-direct/post/start) is wrapped in
 * RoleGate allow=["shipper"] + LRDirectRouteGuard in App.tsx — the same
 * gating the existing posting flow uses. The in-page redirect below mirrors
 * LRDirectPostPage's guard as a second layer.
 */

type WizardMode = "voice" | "type";
type PostType = "load" | "quote";
type Visibility = "private_only" | "open_market";

const STEP_LABELS = ["Mode", "Post Type", "Route", "Dates", "Equipment", "Extras", "Review"] as const;

const FIELD_STEP_META = [
  {
    title: "Route",
    description: "Origin, destination and any extra stops along the way.",
    Icon: MapPin,
    voiceHint: "Atlas will ask “Where is it going?” and fill this in from your answers.",
  },
  {
    title: "Dates",
    description: "Pickup and delivery dates for this freight.",
    Icon: CalendarDays,
    voiceHint: "Atlas will ask when it picks up and delivers.",
  },
  {
    title: "Equipment & Weight",
    description: "Equipment type, length and how heavy the load is.",
    Icon: Truck,
    voiceHint: "Atlas will ask what kind of truck you need.",
  },
  {
    title: "Anything else?",
    description: "Commodity, reference number, notes and requirements — all optional.",
    Icon: ClipboardList,
    voiceHint: "Atlas will ask if there's anything else brokers should know.",
  },
] as const;

export default function LRDirectGuidedWizardPage() {
  usePageMeta({
    title: "Guided Post — LR Direct",
    description: "Step-by-step guided load and quote posting for LR Direct shippers.",
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, isAdmin, isDemoUser } = useAuth();
  const userType = profile?.user_type;
  const { toast } = useToast();
  const {
    defaultEquipment,
    loaded: defaultEquipmentLoaded,
    saving: savingEquipmentDefault,
    justSaved: equipmentDefaultSaved,
    saveDefault: saveEquipmentDefault,
  } = useDefaultEquipment();

  // ── Wizard state (persists across steps — Back never loses data) ──
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<WizardMode | null>(null);
  const [postType, setPostType] = useState<PostType>("load");
  const [visibility, setVisibility] = useState<Visibility>("private_only");
  // Step 1 has the same two phases as the existing start page: type → visibility.
  const [cardPhase, setCardPhase] = useState<1 | 2>(1);
  const [isPosting, setIsPosting] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  // All field data for steps 2–6 — one object, patched per step.
  const [fields, setFields] = useState<WizardFields>(defaultWizardFields);
  // Voice-mode gating: WizardVoiceCapture reports blockers (missing required
  // fields + unresolved "confirm" ambiguities); empty = clean to proceed.
  const [voiceBlockers, setVoiceBlockers] = useState<string[]>([]);

  const patchFields = useCallback((patch: Partial<WizardFields>) => {
    setStepError(null);
    setFields((prev) => ({ ...prev, ...patch }));
  }, []);

  // Pre-fill equipment from the shipper's saved default (once) — mirrors the form.
  const equipmentPrefilledRef = useRef(false);
  useEffect(() => {
    if (equipmentPrefilledRef.current || !defaultEquipmentLoaded || !defaultEquipment) return;
    equipmentPrefilledRef.current = true;
    setFields((prev) => (prev.equipmentType ? prev : { ...prev, equipmentType: defaultEquipment }));
  }, [defaultEquipment, defaultEquipmentLoaded]);

  // Auto-derive the bid deadline from the pickup date (11:59 PM default) until
  // the shipper edits it on the review screen — same rule as the form.
  const bidDeadlineManuallyEditedRef = useRef(false);
  useEffect(() => {
    if (bidDeadlineManuallyEditedRef.current || !fields.pickupDate) return;
    setFields((prev) => {
      const next = `${prev.pickupDate}T23:59`;
      return prev.bidDeadline === next ? prev : { ...prev, bidDeadline: next };
    });
  }, [fields.pickupDate]);

  // Duplicate / template posts are already configured — skip the wizard and go
  // straight to the pre-filled form (identical to the old start page bypass).
  const bypass = !!(searchParams.get("duplicate") || searchParams.get("tpl"));
  useEffect(() => {
    if (!bypass) return;
    const qs = searchParams.toString();
    navigate(`/lr-direct/post${qs ? `?${qs}` : ""}`, { replace: true });
  }, [bypass, searchParams, navigate]);

  // Optional preselection via ?mode=load|quote and ?visibility=private_only|open_market
  // (same params the old start page honored).
  useEffect(() => {
    const m = searchParams.get("mode");
    if (m === "load" || m === "quote") {
      setPostType(m);
      setCardPhase(2);
    }
    const vis = searchParams.get("visibility");
    if (vis === "private_only" || vis === "open_market") {
      setVisibility(vis);
    }
  }, [searchParams]);

  // Private-network targeting params (?network= & ?brokers=) are carried through
  // to the shared save path.
  const networkParam = searchParams.get("network");
  const brokersParam = searchParams.get("brokers");

  // ── Shipper-only guard (second layer behind RoleGate/LRDirectRouteGuard) ──
  if (!bypass && !isAdmin && !isDemoUser && userType && userType !== "shipper") {
    return <Navigate to={userType === "broker" ? "/lr-direct/board" : "/broker-dashboard"} replace />;
  }
  if (bypass) return null;

  const totalSteps = STEP_LABELS.length;

  // ── Progress display: voice mode collapses Route/Dates/Equipment/Extras
  // (internal steps 2–5) into one "Voice" screen → 4 visible steps.
  const isVoice = mode === "voice";
  const displayLabels = isVoice ? (["Mode", "Post Type", "Voice", "Review"] as const) : STEP_LABELS;
  const displayTotal = displayLabels.length;
  const displayStep = isVoice ? (step <= 1 ? step : step >= 6 ? 3 : 2) : step;

  // ── Per-step rule-based validation (same rules as the form's validate()) ──
  const validateStep = (s: number): string | null => {
    // Voice capture screen: the load-being-built card's blockers are the gate
    // (they cover missing required fields + unconfirmed ambiguities).
    if (s === 2 && isVoice && voiceBlockers.length > 0) return voiceBlockers[0];
    if (s === 2) {
      const unresolved = fields.stops.filter((st) => !st.city || !st.state);
      if (unresolved.length > 0) {
        return `Pick a city for every pickup and drop — ${unresolved.length} still need${
          unresolved.length === 1 ? "s" : ""
        } a location. Type a city and tap a suggestion so we can map it.`;
      }
    }
    if (s === 3) {
      if (!fields.pickupDate) return "Choose a pickup date.";
      if (postType === "load" && !fields.deliveryDate) {
        return "Delivery date is required on bid loads — pick one, or go back and post a Quote instead.";
      }
      // Date-order sanity: overall first pickup vs last delivery (per-stop
      // overrides win, falling back to the main dates). Skipped when there's
      // no delivery date (quotes can leave it blank).
      const firstPickupDate =
        fields.stops.find((st) => st.stop_type === "pickup")?.scheduled_date || fields.pickupDate;
      const lastDeliveryDate =
        [...fields.stops].reverse().find((st) => st.stop_type === "delivery")?.scheduled_date ||
        fields.deliveryDate;
      if (firstPickupDate && lastDeliveryDate && lastDeliveryDate < firstPickupDate) {
        return "Delivery date can't be before the pickup date.";
      }
    }
    if (s === 4) {
      if (!fields.equipmentType) return "Choose an equipment type — Van, Reefer and Flatbed are one tap.";
      const w = parseInt(fields.weight, 10);
      if (!fields.weight || isNaN(w) || w <= 0) return "Enter a weight greater than 0 lbs.";
    }
    if (s === 6) {
      if (!fields.bidDeadline) return "A bid deadline is required before posting.";
      const ms = new Date(fields.bidDeadline).getTime();
      if (!Number.isFinite(ms) || ms <= Date.now() + 30 * 60 * 1000) {
        return "The bid deadline must be at least 30 minutes in the future.";
      }
    }
    return null;
  };

  const canGoNext =
    step === 0 ? mode !== null :
    step === 1 ? cardPhase === 2 : // Continue lives inside the card phase
    step === 2 && isVoice ? voiceBlockers.length === 0 :
    step < totalSteps - 1;

  const goBack = () => {
    setStepError(null);
    if (step === 1 && cardPhase === 2) {
      setCardPhase(1);
      return;
    }
    // Voice: Review jumps back to the single capture screen (steps 3–5 don't exist).
    if (isVoice && step === 6) {
      setStep(2);
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  };

  const goNext = () => {
    if (!canGoNext) return;
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      toast({ variant: "destructive", title: "One more thing", description: err });
      return;
    }
    setStepError(null);
    // Voice: capture screen is the only field step — jump straight to Review.
    if (isVoice && step === 2) {
      setStep(6);
      return;
    }
    setStep((s) => Math.min(totalSteps - 1, s + 1));
  };

  const handleExit = () => navigate("/lr-direct");

  // ── POST — the same save path the form uses (saveDirectLoadPost). ──
  const handlePost = async () => {
    if (!user) return;
    // Re-check every rule before the write.
    for (const s of [2, 3, 4, 6]) {
      const err = validateStep(s);
      if (err) {
        setStepError(err);
        toast({ variant: "destructive", title: "One more thing", description: err });
        // Voice: any field error lands back on the capture screen.
        setStep(isVoice && s < 6 ? 2 : s);
        return;
      }
    }

    setIsPosting(true);
    try {
      const pickups = fields.stops.filter((s) => s.stop_type === "pickup");
      const drops = fields.stops.filter((s) => s.stop_type === "delivery");
      const originStop = pickups[0];
      const destStop = drops[drops.length - 1];

      const toPlace = (s: WizardStop): GuidedPlace => ({
        city: s.city,
        state: s.state,
        name: s.name || null,
        address: s.address || null,
        postal_code: s.postal_code || null,
        country: s.country || "US",
        latitude: s.latitude,
        longitude: s.longitude,
      });

      const stops: GuidedStop[] = fields.stops.map((s) => ({
        ...toPlace(s),
        stop_type: s.stop_type,
        scheduled_date:
          s.scheduled_date ||
          (s.stop_type === "pickup" ? fields.pickupDate : fields.deliveryDate || fields.pickupDate),
      }));

      // Mileage — same best-effort compute as the form: multi-stop loads sum
      // the full route; simple lanes go origin → destination.
      const waypoints = fields.stops
        .filter((s) => s.city && s.state && s.latitude != null && s.longitude != null)
        .map((s) => [Number(s.longitude), Number(s.latitude)] as [number, number]);
      let totalMiles: number | null = null;
      let totalMilesSource: "od" | "multistop" | null = null;
      if (waypoints.length > 2) {
        try {
          const r = await getMultiStopRoute(waypoints);
          if (r?.distance) {
            totalMiles = Math.round(r.distance);
            totalMilesSource = "multistop";
          }
        } catch (e) {
          console.warn("[GuidedWizard] multi-stop mileage compute failed", e);
        }
      }
      if ((!totalMiles || totalMiles <= 0) && waypoints.length >= 2) {
        try {
          const r = await getTruckRoute(waypoints[0], waypoints[waypoints.length - 1]);
          if (r?.distance) {
            totalMiles = Math.round(r.distance);
            totalMilesSource = "od";
          }
        } catch (e) {
          console.warn("[GuidedWizard] mileage compute failed", e);
        }
      }

      const result = await saveDirectLoadPost({
        userId: user.id,
        isDemo: isDemoUser === true,
        postType,
        visibility,
        origin: toPlace(originStop),
        destination: toPlace(destStop),
        stops,
        pickupDate: fields.pickupDate || null,
        deliveryDate: fields.deliveryDate || null,
        equipmentType: fields.equipmentType || null,
        weightLbs: parseInt(fields.weight, 10) || null,
        lengthFt: parseFloat(fields.length) || null,
        loadType: fields.loadType,
        loadNature: fields.loadNature,
        commodity: fields.commodity || null,
        referenceNumber: fields.referenceNumber || null,
        comments: fields.comments || null,
        shipperNotes: fields.shipperNotes || null,
        handlingRequirements: fields.handlingRequirements,
        appointmentType: fields.comments.match(/\bappointment required\b/i) ? "appointment" : "fcfs",
        bidDeadline: fields.bidDeadline ? new Date(fields.bidDeadline).toISOString() : null,
        privateNetworkId: networkParam,
        targetBrokerUserIds: brokersParam ? brokersParam.split(",").filter(Boolean) : null,
        totalMiles,
        totalMilesSource,
      });
      toast({
        title: postType === "quote" ? "Quote request posted" : "Load posted",
        description: "Your post is live to brokers.",
      });
      navigate(result.postType === "quote" ? "/lr-direct/my-quotes" : `/lr-direct/live/${result.loadId}`);
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Could not post",
        description: e?.message || "Something went wrong — your entries are still here, try again.",
      });
    } finally {
      setIsPosting(false);
    }
  };

  const modeLabel = mode === "voice" ? "Voice" : mode === "type" ? "Type" : null;

  return (
    <div className="min-h-screen flex flex-col bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border">
        <div className="flex items-center gap-2.5">
          <Zap className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <h1 className="text-base font-semibold tracking-wide uppercase text-foreground">LR Direct</h1>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground font-medium">Guided Post</span>
        </div>
        <button
          type="button"
          onClick={handleExit}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </button>
      </div>

      {/* Progress */}
      <div className="px-4 pt-4 max-w-2xl mx-auto w-full">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Step {displayStep + 1} of {displayTotal}
          </span>
          <span className="text-[11px] font-medium" style={{ color: "hsl(var(--brand-teal-deep))" }}>
            {displayLabels[displayStep]}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${((displayStep + 1) / displayTotal) * 100}%`,
              backgroundColor: "hsl(var(--brand-teal))",
            }}
          />
        </div>
      </div>

      {/* Step body — one focus per screen */}
      <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
        {/* STEP 0 — MODE SELECTOR */}
        {step === 0 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-foreground">How do you want to post?</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Both post the same load — pick whatever's faster for you.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setMode("voice");
                  setStep(1);
                }}
                className={cn(
                  "relative flex flex-col items-start gap-2 rounded-xl border p-5 text-left transition-all",
                  "hover:scale-[1.01] active:scale-[0.99]"
                )}
                style={{
                  background: "linear-gradient(135deg, #0B8A80 0%, #046D66 55%, #02514D 100%)",
                  color: "white",
                  borderColor: mode === "voice" ? "hsl(var(--brand-teal))" : "transparent",
                  boxShadow: mode === "voice" ? "0 0 0 2px hsl(var(--brand-teal) / 0.35)" : undefined,
                }}
                aria-pressed={mode === "voice"}
              >
                <Mic className="h-6 w-6" />
                <div>
                  <p className="text-sm font-bold">Voice</p>
                  <p className="text-[11px] leading-snug opacity-90 mt-0.5">
                    Just say it out loud. Describe your load the way you'd say it — where it's picking up and delivering, when, the equipment, weight, and anything else — and Atlas organizes it into a complete post in seconds. Miss something? Just tap the mic again and add it.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 absolute right-3 bottom-3 opacity-80" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("type");
                  setStep(1);
                }}
                className={cn(
                  "relative flex flex-col items-start gap-2 rounded-xl border p-5 text-left transition-all",
                  "hover:scale-[1.01] active:scale-[0.99]"
                )}
                style={{
                  background: "#FFF9DC",
                  color: "#1a1a1a",
                  borderColor: mode === "type" ? "#F4CE57" : "transparent",
                  boxShadow: mode === "type" ? "0 0 0 2px rgba(244,206,87,0.35)" : undefined,
                }}
                aria-pressed={mode === "type"}
              >
                <PenLine className="h-6 w-6" />
                <div>
                  <p className="text-sm font-bold">Type</p>
                  <p className="text-[11px] leading-snug opacity-90 mt-0.5">
                    Fill it in step by step. We walk you through it one screen at a time — route, dates, equipment, and details — with everything prefilled where we can, so there's nothing to memorize and nothing to miss. Simple and guided from start to finish.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 absolute right-3 bottom-3 opacity-80" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — TYPE SELECTION (the existing 2 cards, reused) */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                {cardPhase === 1 ? "What are you posting?" : "Who can see it?"}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {cardPhase === 1
                  ? "Pick the type of post. You can change this later if needed."
                  : "Choose who can see your post. Your contact info stays hidden until you award."}
              </p>
            </div>

            {cardPhase === 1 ? (
              <PostTypeCardGrid
                value={postType}
                onSelect={(v) => {
                  setPostType(v);
                  setCardPhase(2);
                }}
              />
            ) : (
              <VisibilityCardGrid
                value={visibility}
                onSelect={(v) => {
                  setVisibility(v);
                  setStep(2);
                }}
              />
            )}
          </div>
        )}

        {/* STEPS 2–5 (voice) — ONE capture screen: talk it out, then correct
            by tap or voice on the load-being-built card. */}
        {step >= 2 && step <= 5 && isVoice && (
          <WizardVoiceCapture
            fields={fields}
            onChange={patchFields}
            postType={postType}
            onGateChange={setVoiceBlockers}
            onSwitchToType={() => {
              // Escape hatch — keep everything captured so far.
              setVoiceBlockers([]);
              setStepError(null);
              setMode("type");
            }}
          />
        )}

        {/* STEPS 2–5 (type) — real fields, one focus per screen */}
        {step >= 2 && step <= 5 && !isVoice && (
          <div className="space-y-5">
            {(() => {
              const meta = FIELD_STEP_META[step - 2];
              return (
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2">
                    <meta.Icon className="h-5 w-5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
                    {meta.title}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
                </div>
              );
            })()}

            {step === 2 && (
              <WizardRouteStep fields={fields} onChange={patchFields} userId={user?.id} error={stepError} />
            )}
            {step === 3 && (
              <WizardDatesStep fields={fields} postType={postType} onChange={patchFields} error={stepError} />
            )}
            {step === 4 && (
              <WizardEquipmentStep
                fields={fields}
                onChange={patchFields}
                error={stepError}
                onSaveEquipmentDefault={() => saveEquipmentDefault(fields.equipmentType)}
                savingEquipmentDefault={savingEquipmentDefault}
                equipmentDefaultSaved={equipmentDefaultSaved}
              />
            )}
            {step === 5 && <WizardExtrasStep fields={fields} onChange={patchFields} onSkip={goNext} />}
          </div>
        )}

        {/* STEP 6 — REVIEW + POST */}
        {step === 6 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
                Review &amp; post
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Check everything looks right, then post. Tap Edit on any section to change it.
              </p>
            </div>

            {/* Carried selections */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {modeLabel && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold text-white"
                    style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}>
                    {mode === "voice" ? <Mic className="h-3 w-3" /> : <PenLine className="h-3 w-3" />}
                    {modeLabel} mode
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold text-white"
                  style={{ backgroundColor: "hsl(var(--brand-teal))" }}>
                  {postType === "quote" ? "Quote request" : "Load"}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border border-border text-foreground bg-muted/50">
                  {visibility === "private_only" ? (
                    <>
                      <Lock className="h-3 w-3" /> Private network
                    </>
                  ) : (
                    <>
                      <Globe2 className="h-3 w-3" /> Open market
                    </>
                  )}
                </span>
              </div>
            </div>

            <WizardReviewStep
              fields={fields}
              postType={postType}
              onEditStep={(s) => {
                setStepError(null);
                // Voice: every edit goes back to the single capture screen.
                setStep(isVoice ? 2 : s);
              }}
              onBidDeadlineChange={(v) => {
                bidDeadlineManuallyEditedRef.current = true;
                patchFields({ bidDeadline: v });
              }}
              error={stepError}
            />
            <Button
              onClick={handlePost}
              disabled={isPosting}
              className="w-full h-11 font-semibold"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              {isPosting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Posting…
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-1.5" />
                  Post {postType === "quote" ? "quote request" : "load"}
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Footer nav — sticky on phones so Back/Continue are always reachable
          without scrolling to the bottom of a long form. Desktop unchanged. */}
      <div className="sticky bottom-0 z-20 px-4 py-3 border-t border-border bg-card/95 backdrop-blur-sm [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))] sm:static sm:bg-card sm:backdrop-blur-none sm:[padding-bottom:0.75rem]">
        <div className="max-w-2xl mx-auto w-full min-w-0 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={step === 0 ? handleExit : goBack}
            className="inline-flex min-h-[44px] items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors sm:min-h-0 sm:text-[11px]"
          >
            <ArrowLeft className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
            {step === 0 ? "Exit" : "Back"}
          </button>



          {step >= 2 && step <= 5 && (
            <Button onClick={goNext} disabled={!canGoNext} className="h-10 px-6 font-semibold">
              Continue
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
